import { useMemo, useState } from 'react';
import {
  BodyText,
  Badge,
  Button,
  Card,
  Dialog,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-jurisdiction-rules';
import {
  RefreshCw as RefreshIcon,
  Calculator as CalcIcon,
} from 'lucide-react';
import { useJurisdictions, useJurisdictionFeeSchedule, useCalculateFee } from '../../hooks';
import { formatMoney } from '../../utils/display-format';
import {
  CELL_CONTROL_CLASS,
  FEE_FILING_TYPE_LABEL,
  labelFor,
  optionsFrom,
  SUBMISSION_METHOD_LABEL,
  UccReadFailure,
  UccTable,
  type FeeFilingType,
} from './ucc-display';

type Jurisdiction = components['schemas']['Jurisdiction'];

/**
 * The e-filing wording the Regulatory jurisdiction page uses
 * (JurisdictionDetail.tsx), so one jurisdiction reads the same on both pages.
 */
export function eFilingLabel(hasElectronicFiling: boolean): string {
  return hasElectronicFiling ? 'Available' : 'Not available';
}

const FEE_FILING_TYPE_OPTIONS = optionsFrom(FEE_FILING_TYPE_LABEL);

/**
 * The State cell opens the jurisdiction's fee schedule. It is a button so a
 * keyboard and a screen reader reach it; the row click beside it was
 * mouse-only (r3 op-g5-4).
 */
function useJurisdictionColumns(onOpen: (stateCode: string) => void): ColumnDef<Jurisdiction, unknown>[] {
  return useMemo<ColumnDef<Jurisdiction, unknown>[]>(
    () => [
      {
        accessorKey: 'stateCode',
        header: 'State',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => onOpen(row.original.stateCode)}
            aria-label={`Show the fee schedule for ${row.original.stateName}`}
            className={`font-mono font-medium text-sm ${CELL_CONTROL_CLASS}`}
          >
            {row.original.stateCode}
          </button>
        ),
      },
      { accessorKey: 'stateName', header: 'Name' },
      {
        accessorKey: 'submissionMethods',
        header: 'Filing Methods',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.submissionMethods.map((method) => (
              <Badge key={method} variant="neutral" size="sm">{labelFor(SUBMISSION_METHOD_LABEL, method)}</Badge>
            ))}
          </div>
        ),
      },
      {
        accessorKey: 'hasElectronicFiling',
        header: 'E-Filing',
        cell: ({ row }) => (
          <Badge variant={row.original.hasElectronicFiling ? 'success' : 'neutral'} size="sm">
            {eFilingLabel(row.original.hasElectronicFiling)}
          </Badge>
        ),
      },
    ],
    [onOpen],
  );
}

/**
 * Fee schedule and filing office for the jurisdiction the operator selected,
 * in a dialog. Rendered as a card after the 25-row table and its pager, it
 * mounted below the fold and a selection showed no visible response.
 */
function JurisdictionDetailDialog({ jurisdiction, onClose }: { jurisdiction: Jurisdiction | null; onClose: () => void }) {
  return (
    <Dialog
      open={jurisdiction !== null}
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={jurisdiction ? `${jurisdiction.stateName} (${jurisdiction.stateCode})` : 'Jurisdiction'}
      size="md"
      footer={<Button variant="tertiary" onClick={onClose}>Close</Button>}
    >
      {jurisdiction && <JurisdictionDetail jurisdiction={jurisdiction} />}
    </Dialog>
  );
}

function JurisdictionDetail({ jurisdiction }: { jurisdiction: Jurisdiction }) {
  const feeSchedule = useJurisdictionFeeSchedule(jurisdiction.stateCode);
  const entries = feeSchedule.data?.entries ?? [];

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <BodyText variant="ui" className="text-xs text-tertiary mb-2">Fee Schedule</BodyText>
          {feeSchedule.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : feeSchedule.error ? (
            <BodyText variant="ui" className="text-sm text-error">Could not be read: {feeSchedule.error.message}</BodyText>
          ) : entries.length === 0 ? (
            <BodyText variant="ui" className="text-sm text-tertiary">No fee schedule entries are recorded for this jurisdiction.</BodyText>
          ) : (
            <>
              {entries.map((entry) => (
                <div key={entry.id} className="flex justify-between py-1">
                  <BodyText variant="ui" className="text-sm">{labelFor(FEE_FILING_TYPE_LABEL, entry.filingType)}</BodyText>
                  <BodyText variant="ui" className="text-sm font-medium tabular-nums">{formatMoney(entry.baseFee)}</BodyText>
                </div>
              ))}
              {feeSchedule.data?.sourceStatute && (
                <BodyText variant="ui" className="text-xs text-tertiary mt-2">Source: {feeSchedule.data.sourceStatute}</BodyText>
              )}
            </>
          )}
        </div>
        <div>
          <BodyText variant="ui" className="text-xs text-tertiary mb-2">Filing Office</BodyText>
          <BodyText variant="ui" className="text-sm">{jurisdiction.filingOfficeName}</BodyText>
          {jurisdiction.electronicFilingPortalUrl && (
            <a
              href={jurisdiction.electronicFilingPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-sans text-secondary underline"
            >
              Electronic filing portal
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export function UCCJurisdictionsPage() {
  const { data, isLoading, error, refetch } = useJurisdictions();
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [calcForm, setCalcForm] = useState<{ stateCode: string; filingType: FeeFilingType; pages: string; additionalDebtors: string }>({
    stateCode: '',
    filingType: 'UCC1',
    pages: '1',
    additionalDebtors: '0',
  });
  const calculateFee = useCalculateFee();
  const columns = useJurisdictionColumns(setSelectedCode);

  const jurisdictionList = data?.data ?? [];
  const selected = jurisdictionList.find((j) => j.stateCode === selectedCode) ?? null;
  const stateOptions = jurisdictionList.map((j) => ({ value: j.stateCode, label: `${j.stateName} (${j.stateCode})` }));

  const handleCalculate = async () => {
    if (!calcForm.stateCode) return;
    try {
      await calculateFee.mutateAsync({
        stateCode: calcForm.stateCode,
        body: {
          filingType: calcForm.filingType,
          pageCount: parseInt(calcForm.pages, 10) || 1,
          additionalDebtorCount: parseInt(calcForm.additionalDebtors, 10) || 0,
        },
      });
    } catch {
      // The dialog renders calculateFee.error.
    }
  };

  return (
    <div>
      <PageHeader
        description="UCC filing jurisdiction rules and fee schedules. Select a jurisdiction to see its fee schedule."
        actions={
          <>
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshIcon className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} size={16} />
              Refresh
            </Button>
            <Button variant="primary" onClick={() => setCalcDialogOpen(true)}>
              <CalcIcon className="mr-2" size={16} />
              Fee Calculator
            </Button>
          </>
        }
      />

      <UccTable<Jurisdiction>
        columns={columns}
        rows={jurisdictionList}
        isLoading={isLoading}
        error={error}
        errorTitle="Could not load jurisdictions"
        onRetry={() => refetch()}
        empty={{
          title: 'No jurisdictions configured',
          description: 'Jurisdiction rules and fee schedules are seeded during platform provisioning.',
        }}
        getRowId={(row) => row.stateCode}
        onRowClick={(row) => setSelectedCode(row.stateCode)}
      />

      <JurisdictionDetailDialog jurisdiction={selected} onClose={() => setSelectedCode(null)} />

      <Dialog
        open={calcDialogOpen}
        onOpenChange={(open) => { if (!open) setCalcDialogOpen(false); }}
        title="Fee Calculator"
        size="sm"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setCalcDialogOpen(false)}>Close</Button>
            <Button variant="primary" onClick={handleCalculate} disabled={calculateFee.isPending || !calcForm.stateCode}>
              {calculateFee.isPending ? 'Calculating...' : 'Calculate'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Jurisdiction"
            options={stateOptions}
            placeholder={error ? 'Jurisdictions could not be read' : 'Select a jurisdiction'}
            value={calcForm.stateCode}
            onChange={(e) => setCalcForm(prev => ({ ...prev, stateCode: e.target.value }))}
            required
          />
          <Select
            label="Filing Type"
            options={FEE_FILING_TYPE_OPTIONS}
            value={calcForm.filingType}
            onChange={(e) => setCalcForm(prev => ({ ...prev, filingType: e.target.value as FeeFilingType }))}
          />
          <Input
            label="Pages"
            type="number"
            min={1}
            value={calcForm.pages}
            onChange={(e) => setCalcForm(prev => ({ ...prev, pages: e.target.value }))}
          />
          <Input
            label="Additional Debtors"
            type="number"
            min={0}
            value={calcForm.additionalDebtors}
            onChange={(e) => setCalcForm(prev => ({ ...prev, additionalDebtors: e.target.value }))}
          />
          {calculateFee.data && (
            <Card className="p-4 bg-elevated">
              <BodyText variant="ui" className="text-xs text-tertiary">Calculated Fee</BodyText>
              <BodyText variant="ui" className="text-2xl font-semibold tabular-nums">
                {formatMoney(calculateFee.data.totalFee)}
              </BodyText>
              {calculateFee.data.breakdown.map((item) => (
                <div key={item.component} className="flex justify-between py-1">
                  <BodyText variant="ui" className="text-sm text-secondary">{item.description}</BodyText>
                  <BodyText variant="ui" className="text-sm tabular-nums">{formatMoney(item.amount)}</BodyText>
                </div>
              ))}
            </Card>
          )}
          {calculateFee.error && (
            <UccReadFailure title="Fee could not be calculated" error={calculateFee.error} />
          )}
        </div>
      </Dialog>
    </div>
  );
}
